import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { KNORIA_LOGO_URL } from "../components/KnoriaLogo";
import {
  VideoCamera, Brain, ChatCircle, Lightning, Trophy, Exam,
  Shield, Rocket, ArrowRight, CheckCircle, Star, ChartLine,
  Target, Sparkle, Users, Certificate, Play, Check, X
} from "@phosphor-icons/react";
import { useLang } from "../contexts/LanguageContext";
import { PublicLangToggle } from "../components/PublicLangToggle";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

// Tâche 1394 — Les deux langues sur la même entrée (motif 1324b) : ces
// tables vivent hors composant, donc hors du hook. Les accents français
// manquants (« video », « repond », « meme ») sont restaurés au passage :
// c'est la première page que voit un prospect.
const FEATURES = [
  { icon: VideoCamera, title: "Extraction IA automatique", title_en: "Automatic AI extraction", desc: "Déposez une vidéo. L'IA transcrit, identifie les locuteurs et extrait Q&R, procédures, scripts et objections. En moins de 5 minutes.", desc_en: "Drop in a video. The AI transcribes it, identifies the speakers and pulls out Q&A, procedures, scripts and objections. In under 5 minutes.", color: "#3b82f6" },
  { icon: ChatCircle, title: "Chat RAG avec citations", title_en: "RAG chat with citations", desc: "Posez une question. KNORIA répond avec le moment exact dans la vidéo. Comme Google, mais pour votre expertise interne.", desc_en: "Ask a question. KNORIA answers with the exact moment in the video. Like Google, but for your own expertise.", color: "#8b5cf6" },
  { icon: Exam, title: "Quiz auto-générés", title_en: "Auto-generated quizzes", desc: "L'IA crée des quiz basés sur vos vidéos. Chaque question cite sa source. Validation des connaissances en temps réel.", desc_en: "The AI builds quizzes from your videos. Every question cites its source. Knowledge checked as it happens.", color: "#10b981" },
  { icon: Sparkle, title: "Vidéo avatar IA", title_en: "AI avatar video", desc: "Photo du PDG + PDF = vidéo de formation professionnelle. Votre expert parle même quand il n'est pas disponible.", desc_en: "CEO photo + PDF = a professional training video. Your expert speaks even when they are not available.", color: "#f59e0b" },
  { icon: Certificate, title: "Certifications & conformité", title_en: "Certifications & compliance", desc: "Certifications avec expiration automatique (12 mois). Alertes de renouvellement. Parfait pour la conformité réglementaire.", desc_en: "Certifications that expire on their own (12 months). Renewal alerts. Built for regulatory compliance.", color: "#ef4444" },
  { icon: ChartLine, title: "Analytique d'équipe en temps réel", title_en: "Real-time team analytics", desc: "Le PDG voit qui a complété sa formation, les scores, les lacunes. Décisions basées sur les données, pas les impressions.", desc_en: "The CEO sees who finished their training, the scores, the gaps. Decisions on data, not impressions.", color: "#06b6d4" },
];

const COMPARISON = [
  { feature: "Extraction IA automatique de vidéos", feature_en: "Automatic AI extraction from video", knoria: true, synthesia: false, talentlms: false, guru: false },
  { feature: "Chat RAG avec citations vidéo", feature_en: "RAG chat with video citations", knoria: true, synthesia: false, talentlms: false, guru: true },
  { feature: "Génération d'avatar IA (photo → vidéo)", feature_en: "AI avatar generation (photo → video)", knoria: true, synthesia: true, talentlms: false, guru: false },
  { feature: "Quiz auto-générés par IA", feature_en: "AI-generated quizzes", knoria: true, synthesia: false, talentlms: true, guru: false },
  { feature: "Certifications avec expiration", feature_en: "Certifications that expire", knoria: true, synthesia: false, talentlms: true, guru: false },
  { feature: "Ludification & classement", feature_en: "Gamification & leaderboard", knoria: true, synthesia: false, talentlms: true, guru: false },
  { feature: "Bilingue FR/EN natif", feature_en: "Natively bilingual FR/EN", knoria: true, synthesia: true, talentlms: false, guru: false },
  { feature: "Analytique d'équipe avancée", feature_en: "Advanced team analytics", knoria: true, synthesia: false, talentlms: true, guru: true },
  { feature: "Tout-en-un (pas 3 outils)", feature_en: "All in one (not 3 tools)", knoria: true, synthesia: false, talentlms: false, guru: false },
  { feature: "Prix < 600 $/mois (50 personnes)", feature_en: "Under $600/month (50 people)", knoria: true, synthesia: false, talentlms: false, guru: false },
];

const Cell = ({ ok }) => ok ? <Check size={18} weight="bold" className="text-emerald-400 mx-auto" /> : <X size={18} weight="bold" className="text-slate-600 mx-auto" />;

export default function LandingPage() {
  const { tr, lang } = useLang();
  return (
    <div className="text-[#F8FAFC] min-h-screen overflow-hidden" style={{ fontFamily: "'Manrope', sans-serif", backgroundColor: "#000000" }}>
      <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Outfit:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── Navbar ── */}
      <nav className="fixed w-full z-50 backdrop-blur-xl bg-black/70 border-b border-white/5" data-testid="landing-navbar">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div />
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-slate-300 hover:text-white transition-colors px-4 py-2" data-testid="nav-login">{tr("Connexion", "Sign in")}</Link>
            <Link to="/pricing" className="bg-white text-[#030712] hover:bg-slate-200 text-sm font-bold px-5 py-2.5 rounded-lg transition-all hover:-translate-y-0.5 shadow-[0_0_20px_rgba(255,255,255,0.15)]" data-testid="nav-cta">
              {tr("Commencer", "Get started")}
            </Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="min-h-[90vh] relative flex flex-col items-center justify-center pt-20" data-testid="hero-section">
        <div className="absolute inset-0" style={{ background: "#000000" }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-[#002FA7]/8 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#38BDF8]/5 rounded-full blur-[120px]" />

        <motion.div className="relative z-10 w-full" initial="hidden" animate="visible" variants={stagger}>
          {/* Tâche 1394 — Sans bascule, la version anglaise de cette page
              existe mais reste inatteignable : un prospect anglophone
              tomberait sur du français. Même leçon qu'en 1324 pour l'écran
              de connexion. */}
          <div className="absolute top-4 right-4 z-20">
            <PublicLangToggle tone="dark" />
          </div>

          {/* Logo KNORIA — gros, sans fond visible */}
          <motion.div variants={fadeUp} className="mb-6">
            <img src={KNORIA_LOGO_URL} alt="KNORIA" className="h-56 md:h-80 lg:h-[420px] xl:h-[500px] mx-auto object-contain" data-testid="hero-logo" />
          </motion.div>

          <div className="max-w-5xl mx-auto text-center px-6">

          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-6">
            <Lightning size={14} className="text-[#38BDF8]" /> {tr("Propulsé par GPT-5.2 + Whisper", "Powered by GPT-5.2 + Whisper")}
          </motion.div>

          <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[0.95] mb-6">
            {tr("Extrayez l'intelligence.", "Extract the intelligence.")}<br />
            <span className="bg-gradient-to-r from-[#002FA7] via-[#38BDF8] to-[#002FA7] bg-clip-text text-transparent">{tr("Écrasez la concurrence.", "Outrun the competition.")}</span>
          </motion.h1>

          <motion.p variants={fadeUp} className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed">
            {tr("La plateforme qui transforme vos vidéos de formation en connaissances exploitables par l’IA. Transcription, extraction, quiz, clavardage — automatiquement.", "The platform that turns your training videos into knowledge the AI can work with. Transcription, extraction, quizzes, chat — automatically.")}
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/pricing" className="bg-white text-[#030712] hover:bg-slate-200 font-bold px-8 py-3.5 rounded-lg text-base transition-all hover:-translate-y-1 shadow-[0_0_30px_rgba(255,255,255,0.2)] flex items-center gap-2" data-testid="hero-cta">
              {tr("Commencer l’essai", "Start the trial")} <ArrowRight size={18} weight="bold" />
            </Link>
            <a href="#interactive-demo" className="text-slate-300 hover:text-white font-semibold px-8 py-3.5 rounded-lg border border-white/10 hover:border-white/30 transition-all flex items-center gap-2" data-testid="hero-demo">
              <Play size={18} weight="fill" /> {tr("Voir la démo", "Watch the demo")}
            </a>
          </motion.div>

          {/* Canada trust bar */}
          <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center justify-center gap-3 md:gap-5" data-testid="hero-canada-bar">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-red-500/20 bg-red-500/5">
              <span className="text-base">🍁</span>
              <span className="text-xs font-bold text-red-400">{tr("Données 100 % au Canada", "100% of data in Canada")}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-full border border-white/5 bg-white/[0.02]">
              <Shield size={14} className="text-emerald-400" weight="fill" />
              <span className="text-[11px] text-slate-400 font-medium">{tr("Conforme Loi 25", "Law 25 compliant")}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-full border border-white/5 bg-white/[0.02]">
              <Shield size={14} className="text-emerald-400" weight="fill" />
              <span className="text-[11px] text-slate-400 font-medium">SOC 2 + ISO 27001</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-full border border-white/5 bg-white/[0.02]">
              <Shield size={14} className="text-emerald-400" weight="fill" />
              <span className="text-[11px] text-slate-400 font-medium">{tr("Chiffrement AES-256", "AES-256 encryption")}</span>
            </div>
          </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ── ROI Stats ── */}
      <section className="py-16 border-y border-white/5" data-testid="roi-section">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          {[
            { value: "3x", label: tr("Intégration plus rapide", "Faster onboarding"), sub: tr("vs formation traditionnelle", "vs traditional training") },
            { value: "80%", label: tr("Rétention des connaissances", "Knowledge retention"), sub: tr("grâce au quiz adaptatif", "thanks to the adaptive quiz") },
            { value: "10h+", label: tr("Économisées par personne et par mois", "Saved per person per month"), sub: tr("recherche instantanée plutôt que manuelle", "instant search instead of hunting") },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}>
              <div className="text-5xl md:text-6xl font-extrabold tracking-tighter bg-gradient-to-b from-white to-slate-400 bg-clip-text text-transparent">{s.value}</div>
              <div className="text-sm font-bold text-white mt-2">{s.label}</div>
              <div className="text-xs text-slate-500 mt-1">{s.sub}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="py-24 md:py-32 px-6 md:px-12" id="demo" data-testid="how-it-works">
        <div className="max-w-6xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">{tr("Comment ça marche", "How it works")}</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">{tr("3 étapes. Zéro effort.", "3 steps. Zero effort.")}</motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "01", title: tr("Importez", "Upload"), desc: tr("Chargez vos vidéos de formation brutes. Réunions, webinaires, formations internes — tout format accepté.", "Drop in your raw training videos. Meetings, webinars, internal sessions — any format works."), icon: VideoCamera, color: "#3b82f6" },
              { step: "02", title: tr("L'IA extrait", "The AI extracts"), desc: tr("Notre moteur transcrit, identifie les locuteurs et structure automatiquement les concepts clés, les procédures et les Q&R.", "Our engine transcribes, identifies the speakers and automatically structures the key concepts, procedures and Q&A."), icon: Brain, color: "#8b5cf6" },
              { step: "03", title: tr("L'équipe apprend", "The team learns"), desc: tr("Quiz générés par IA, clavardage avec citations vidéo, parcours d'intégration — vos employés montent en compétence trois fois plus vite.", "AI-generated quizzes, chat with video citations, onboarding pathways — your people get up to speed three times faster."), icon: Rocket, color: "#10b981" },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.2 }}
                className="relative bg-[#0F172A] border border-white/5 rounded-2xl p-8 md:p-10 hover:border-white/20 transition-colors group">
                <div className="text-[80px] font-black text-white/[0.03] absolute top-4 right-6 leading-none">{s.step}</div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${s.color}15` }}>
                  <s.icon size={24} style={{ color: s.color }} weight="duotone" />
                </div>
                <h3 className="text-xl font-bold mb-2">{s.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Interactive Demo ── */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0F172A]/50 relative overflow-hidden" id="interactive-demo" data-testid="interactive-demo">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#002FA7]/10 rounded-full blur-[150px]" />
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] font-bold text-[#38BDF8] mb-3">{tr("Démo interactive", "Interactive demo")}</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">{tr("Voyez KNORIA en action", "See KNORIA at work")}</motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mt-3 max-w-xl mx-auto">{tr("Explorez un exemple réel de traitement vidéo — du dépôt du fichier à la base de connaissances.", "Walk through a real video being processed — from the upload to the knowledge base.")}</motion.p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="bg-[#0a1628] border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
            {/* Demo Header */}
            <div className="flex items-center gap-3 px-6 py-3 border-b border-white/10 bg-[#08123A]">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-green-500/80" />
              </div>
              <span className="text-xs text-slate-500 font-mono">app.knoria.ai/dashboard</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Left — Pipeline Simulation */}
              <div className="p-8 border-r border-white/5">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#002FA7]/20 flex items-center justify-center">
                    <VideoCamera size={20} className="text-[#38BDF8]" weight="duotone" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white">Pipeline IA — "Formation Conseiller"</div>
                    <div className="text-xs text-slate-500">{tr("43:22 de vidéo traitée en 4 min 18 s", "43:22 of video processed in 4 min 18 s")}</div>
                  </div>
                </div>

                <div className="space-y-4">
                  {[
                    { label: tr("Extraction audio", "Audio extraction"), time: "12s", done: true },
                    { label: tr("Transcription Whisper", "Whisper transcription"), time: "1m 42s", done: true },
                    { label: tr("Diarisation (3 locuteurs)", "Diarisation (3 speakers)"), time: "34s", done: true },
                    { label: tr("Extraction GPT-5.2", "GPT-5.2 extraction"), time: "1m 50s", done: true },
                    { label: tr("Embeddings RAG", "RAG embeddings"), time: "8s", done: true },
                  ].map((step, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle size={18} weight="fill" className="text-emerald-400 flex-shrink-0" />
                      <div className="flex-1 text-sm text-slate-300">{step.label}</div>
                      <span className="text-xs text-slate-500 font-mono">{step.time}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                  <div className="flex items-center gap-2 text-emerald-400 text-sm font-bold mb-2">
                    <Lightning size={16} weight="fill" /> {tr("Résultats extraits", "Extracted results")}
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    {[
                      { n: "23", label: "Q&A" },
                      { n: "8", label: tr("Procédures", "Procedures") },
                      { n: "12", label: "Scripts" },
                    ].map((r, i) => (
                      <div key={i} className="bg-emerald-500/5 rounded-lg py-2">
                        <div className="text-lg font-bold text-white">{r.n}</div>
                        <div className="text-[10px] text-emerald-400/70">{r.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right — Chat Demo */}
              <div className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#8b5cf6]/20 flex items-center justify-center">
                    <ChatCircle size={20} className="text-[#8b5cf6]" weight="duotone" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white">Chat RAG — Demander a KNORIA</div>
                    <div className="text-xs text-slate-500">{tr("Réponses citant la vidéo source", "Answers that cite the source video")}</div>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* User question */}
                  <div className="flex justify-end">
                    <div className="bg-[#002FA7] rounded-xl rounded-br-sm px-4 py-2.5 max-w-[85%]">
                      <p className="text-sm text-white">{tr("Quelle est la procédure pour ouvrir un REER à un nouveau client ?", "What is the procedure to open an RRSP for a new client?")}</p>
                    </div>
                  </div>

                  {/* AI answer */}
                  <div className="flex justify-start">
                    <div className="bg-white/5 border border-white/10 rounded-xl rounded-bl-sm px-4 py-3 max-w-[90%]">
                      <p className="text-sm text-slate-200 leading-relaxed mb-3">{tr("Selon la procédure extraite de la vidéo ", "Based on the procedure extracted from the video ")}<span className="text-[#38BDF8] font-medium">{tr("« Formation nouveaux conseillers »", "“New advisor training”")}</span>{tr(", les étapes sont :", ", the steps are:")}</p>
                      <ol className="text-sm text-slate-300 space-y-1.5 ml-4 mb-3 list-decimal">
                        <li>{tr("Vérifier l’identité du client (2 pièces)", "Check the client’s identity (2 documents)")}</li>
                        <li>{tr("Évaluer le profil d’investisseur (KYC)", "Assess the investor profile (KYC)")}</li>
                        <li>{tr("Présenter les options REER disponibles", "Present the available RRSP options")}</li>
                        <li>{tr("Faire signer le formulaire d’ouverture", "Have the opening form signed")}</li>
                      </ol>
                      <div className="flex items-center gap-2 text-xs text-[#38BDF8] mt-2 px-2 py-1 rounded bg-[#38BDF8]/5 w-fit">
                        <Play size={12} weight="fill" /> {tr("Source : Formation Conseiller — 12:34", "Source: Advisor Training — 12:34")}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex gap-2">
                  <div className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-slate-500">
                    {tr("Posez votre question…", "Ask your question…")}
                  </div>
                  <div className="bg-[#002FA7] rounded-lg px-4 py-2.5 flex items-center">
                    <ArrowRight size={16} className="text-white" weight="bold" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mt-8">
            <Link to="/register" className="inline-flex items-center gap-2 text-[#38BDF8] hover:text-white font-bold text-sm transition-colors" data-testid="demo-cta">
              Essayez avec vos propres videos <ArrowRight size={16} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── Features Grid ── */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0F172A]/50" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">{tr("Fonctionnalités", "Features")}</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">{tr("Un seul outil. Trois remplacements.", "One tool. Three replaced.")}</motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mt-3 max-w-xl mx-auto">{tr("KNORIA remplace votre LMS, votre base de connaissances et votre générateur vidéo. Pour trois fois moins cher.", "KNORIA replaces your LMS, your knowledge base and your video generator. For three times less.")}</motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="bg-[#0F172A] border border-white/5 rounded-xl p-8 hover:border-white/15 transition-all hover:-translate-y-1 group" data-testid={`feature-card-${i}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: `${f.color}15` }}>
                  <f.icon size={20} style={{ color: f.color }} weight="duotone" />
                </div>
                <h3 className="text-base font-bold mb-2">{lang === "en" ? f.title_en : f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{lang === "en" ? f.desc_en : f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Competition Table ── */}
      <section className="py-24 md:py-32 px-6 md:px-12" data-testid="comparison-section">
        <div className="max-w-5xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">{tr("Comparaison", "Comparison")}</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">{tr("Pourquoi payer pour trois outils ?", "Why pay for three tools?")}</motion.h2>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-[#0F172A]">
                  <th className="text-left p-4 font-semibold text-slate-300">{tr("Fonctionnalité", "Feature")}</th>
                  <th className="text-center p-4 font-bold text-white bg-[#002FA7]/20"><img src={KNORIA_LOGO_URL} alt="KNORIA" className="h-8 mx-auto object-contain" /></th>
                  <th className="text-center p-4 font-semibold text-slate-400">Synthesia</th>
                  <th className="text-center p-4 font-semibold text-slate-400">TalentLMS</th>
                  <th className="text-center p-4 font-semibold text-slate-400">Guru</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                    <td className="p-4 text-slate-300">{row.feature}</td>
                    <td className="p-4 bg-[#002FA7]/5"><Cell ok={row.knoria} /></td>
                    <td className="p-4"><Cell ok={row.synthesia} /></td>
                    <td className="p-4"><Cell ok={row.talentlms} /></td>
                    <td className="p-4"><Cell ok={row.guru} /></td>
                  </tr>
                ))}
                <tr className="bg-[#0F172A]">
                  <td className="p-4 font-bold">Prix (50 utilisateurs)</td>
                  <td className="p-4 text-center font-bold text-emerald-400 bg-[#002FA7]/10">599$/mois</td>
                  <td className="p-4 text-center text-slate-400">1,000$+</td>
                  <td className="p-4 text-center text-slate-400">500$+</td>
                  <td className="p-4 text-center text-slate-400">625$+</td>
                </tr>
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* ── Testimonial ── */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0F172A]/30" data-testid="testimonial-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
            <div className="flex justify-center gap-1 mb-6">
              {[1,2,3,4,5].map(i => <Star key={i} size={20} weight="fill" className="text-amber-400" />)}
            </div>
            <blockquote className="text-xl md:text-2xl font-medium leading-relaxed text-slate-200 mb-8">
              {tr(
                "« En 3 semaines, on a numérisé 2 ans de formation interne. Nos nouveaux conseillers sont opérationnels en 5 jours au lieu de 3 semaines. KNORIA a changé notre façon de transmettre l’expertise. »",
                "“In 3 weeks we digitised 2 years of internal training. Our new advisors are up to speed in 5 days instead of 3. KNORIA changed the way we pass expertise on.”",
              )}
            </blockquote>
            <div className="flex items-center justify-center gap-4">
              <img src="https://images.unsplash.com/photo-1584940120505-117038d90b05?w=80&h=80&fit=crop&crop=face" alt="" className="w-12 h-12 rounded-full object-cover border-2 border-white/10" />
              <div className="text-left">
                <div className="font-bold text-sm">Marc-Andre Leblanc</div>
                <div className="text-xs text-slate-400">{tr("Président, Groupe Financier Horizon", "President, Groupe Financier Horizon")}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── DONNÉES 100% AU CANADA ── */}
      <section className="py-24 md:py-32 px-6 md:px-12 relative overflow-hidden" data-testid="canada-section">
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #0F172A 0%, #1a0a0a 50%, #0F172A 100%)" }} />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-600/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-red-600/5 rounded-full blur-[120px]" />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            
            {/* Canadian badge */}
            <motion.div variants={fadeUp} className="flex justify-center mb-8">
              <div className="inline-flex items-center gap-3 px-6 py-2.5 rounded-full border border-red-500/20 bg-red-500/5">
                <span className="text-2xl">🍁</span>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-red-400">{tr("Fièrement canadien", "Proudly Canadian")}</span>
                <span className="text-2xl">🍁</span>
              </div>
            </motion.div>

            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-center mb-4">
              {tr("Vos données restent au", "Your data stays in")}
              <span className="bg-gradient-to-r from-red-500 via-white to-red-500 bg-clip-text text-transparent"> {tr("Canada", "Canada")}</span>.
              <br />{tr("Point final.", "Full stop.")}
            </motion.h2>

            <motion.p variants={fadeUp} className="text-slate-400 text-lg text-center max-w-2xl mx-auto mb-14">
              {tr("Toutes vos données — vidéos, transcriptions, connaissances, messages, métadonnées — sont stockées à", "All your data — videos, transcripts, knowledge, messages, metadata — is stored in")}
              <strong className="text-white"> {tr("Beauharnois, Québec", "Beauharnois, Quebec")}</strong>. {tr("Pas aux États-Unis. Pas en Europe. Ici.", "Not in the United States. Not in Europe. Here.")}
              <span className="block mt-3 text-sm text-emerald-400 font-semibold">{tr("Infrastructure 100 % québécoise. Stockage S3 OVH + base de données MongoDB OVH — tous deux à Beauharnois.", "100% Quebec infrastructure. OVH S3 storage + OVH MongoDB database — both in Beauharnois.")}</span>
            </motion.p>

            {/* Cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
              <motion.div variants={fadeUp} className="bg-black/40 backdrop-blur-sm border border-red-500/10 rounded-2xl p-8 text-center hover:border-red-500/30 transition-all hover:-translate-y-1">
                <div className="w-14 h-14 mx-auto rounded-xl bg-red-500/10 flex items-center justify-center mb-4">
                  <Shield size={28} className="text-red-400" weight="duotone" />
                </div>
                <h3 className="text-lg font-bold mb-2">{tr("Conforme Loi 25", "Law 25 compliant")}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{tr("Protection des renseignements personnels selon la Loi 25 du Québec, la LPRPSP et la LPRPDE. Vos clients sont protégés.", "Personal information protected under Quebec’s Law 25, the LPRPSP and PIPEDA. Your clients are covered.")}</p>
              </motion.div>

              <motion.div variants={fadeUp} className="bg-black/40 backdrop-blur-sm border border-red-500/10 rounded-2xl p-8 text-center hover:border-red-500/30 transition-all hover:-translate-y-1">
                <div className="w-14 h-14 mx-auto rounded-xl bg-red-500/10 flex items-center justify-center mb-4">
                  <Target size={28} className="text-red-400" weight="duotone" />
                </div>
                <h3 className="text-lg font-bold mb-2">{tr("Certifié SOC 2 + ISO 27001", "SOC 2 + ISO 27001 certified")}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{tr("Infrastructure auditée indépendamment. Chiffrement AES-256 au repos, TLS 1.3 en transit. Zéro compromis sur la sécurité.", "Independently audited infrastructure. AES-256 at rest, TLS 1.3 in transit. No compromise on security.")}</p>
              </motion.div>

              <motion.div variants={fadeUp} className="bg-black/40 backdrop-blur-sm border border-red-500/10 rounded-2xl p-8 text-center hover:border-red-500/30 transition-all hover:-translate-y-1">
                <div className="w-14 h-14 mx-auto rounded-xl bg-red-500/10 flex items-center justify-center mb-4">
                  <Users size={28} className="text-red-400" weight="duotone" />
                </div>
                <h3 className="text-lg font-bold mb-2">{tr("Conçu pour les cabinets", "Built for professional firms")}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{tr("Cabinets d’avocats, conseillers financiers, firmes comptables — vos données sensibles méritent une plateforme canadienne.", "Law firms, financial advisors, accounting practices — your sensitive data deserves a Canadian platform.")}</p>
              </motion.div>
            </div>

            {/* Trust badges */}
            <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-4 mb-10">
              {[
                { label: "Loi 25", sub: "Quebec" },
                { label: "PIPEDA", sub: "Canada" },
                { label: "SOC 2", sub: "Type II" },
                { label: "ISO 27001", sub: tr("Certifié", "Certified") },
                { label: "AES-256", sub: tr("Chiffrement", "Encryption") },
                { label: "TLS 1.3", sub: tr("En transit", "In transit") },
              ].map((b, i) => (
                <div key={i} className="flex flex-col items-center px-5 py-3 rounded-xl border border-white/5 bg-white/[0.02]">
                  <span className="text-sm font-bold text-white">{b.label}</span>
                  <span className="text-[10px] text-slate-500">{b.sub}</span>
                </div>
              ))}
            </motion.div>

            {/* Quote */}
            <motion.div variants={fadeUp} className="max-w-3xl mx-auto text-center">
              <div className="bg-black/40 border border-white/5 rounded-2xl p-8 md:p-10">
                <p className="text-base md:text-lg text-slate-300 italic leading-relaxed mb-4">
                  {tr(
                    "« Pour nos clients, la confidentialité n’est pas négociable. Quand on leur dit que leurs données de formation ne quittent jamais le Canada, c’est un argument qui ferme la vente. »",
                    "“For our clients, confidentiality is not negotiable. Telling them their training data never leaves Canada is what closes the sale.”",
                  )}
                </p>
                <div className="flex items-center justify-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-sm font-bold text-white">{tr("Stockage exclusif — Beauharnois, Québec, Canada", "Stored only in Beauharnois, Quebec, Canada")}</span>
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Pricing Preview ── */}
      <section className="py-24 md:py-32 px-6 md:px-12" data-testid="pricing-preview">
        <div className="max-w-5xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">{tr("Tarification", "Pricing")}</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">{tr("Simple. Transparent. Sans surprise.", "Simple. Transparent. No surprises.")}</motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "Starter", price: "$249", per: tr("/mois", "/month"), users: tr("10 personnes", "10 people"), features: [tr("Extraction IA", "AI extraction"), tr("Chat RAG", "RAG chat"), tr("Quiz", "Quizzes"), tr("Bilingue FR/EN", "Bilingual FR/EN")], popular: false },
              { name: "Business", price: "$599", per: tr("/mois", "/month"), users: tr("50 personnes", "50 people"), features: [tr("Tout Starter +", "Everything in Starter +"), tr("Avatar vidéo IA", "AI video avatar"), tr("Analytique avancée", "Advanced analytics"), tr("Coach IA", "AI coach"), tr("Ludification", "Gamification")], popular: true },
              { name: "Enterprise", price: "$1,199", per: tr("/mois", "/month"), users: tr("200 personnes", "200 people"), features: [tr("Tout Business +", "Everything in Business +"), tr("Accès API", "API access"), tr("Support prioritaire", "Priority support"), tr("Image de marque sur mesure", "Custom branding")], popular: false },
            ].map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                className={`relative rounded-2xl p-8 border transition-all hover:-translate-y-1 ${p.popular ? "bg-[#002FA7]/10 border-[#002FA7]/50 shadow-[0_0_40px_rgba(0,47,167,0.15)]" : "bg-[#0F172A] border-white/5"}`}
                data-testid={`pricing-${p.name.toLowerCase()}`}>
                {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#002FA7] text-white text-xs font-bold px-4 py-1 rounded-full">{tr("POPULAIRE", "POPULAR")}</div>}
                <h3 className="text-lg font-bold mb-1">{p.name}</h3>
                <p className="text-xs text-slate-400 mb-4">{p.users}</p>
                <div className="flex items-end gap-1 mb-6">
                  <span className="text-4xl font-extrabold tracking-tight">{p.price}</span>
                  <span className="text-slate-400 text-sm mb-1">{p.per}</span>
                </div>
                <ul className="space-y-2.5 mb-8">
                  {p.features.map((f, fi) => (
                    <li key={fi} className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle size={16} weight="fill" className="text-emerald-400 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/pricing"
                  className={`block w-full text-center py-3 rounded-lg font-bold text-sm transition-all hover:-translate-y-0.5 ${p.popular ? "bg-white text-[#030712] hover:bg-slate-200 shadow-[0_0_20px_rgba(255,255,255,0.15)]" : "bg-[#002FA7] text-white hover:bg-[#002280]"}`}
                  data-testid={`pricing-cta-${p.name.toLowerCase()}`}>
                  {tr("Commencer", "Get started")} <ArrowRight size={14} className="inline ml-1" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-24 md:py-32 px-6 relative overflow-hidden" data-testid="final-cta">
        <div className="absolute inset-0 bg-gradient-to-r from-[#002FA7]/20 to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#002FA7]/10 rounded-full blur-[150px]" />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">{tr("Prêt à transformer votre formation ?", "Ready to transform your training?")}</h2>
          <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">{tr("Rejoignez les leaders financiers qui forment leurs équipes trois fois plus vite avec KNORIA.", "Join the financial leaders training their teams three times faster with KNORIA.")}</p>
          <Link to="/pricing" className="inline-flex items-center gap-2 bg-white text-[#030712] hover:bg-slate-200 font-bold px-8 py-4 rounded-lg text-base transition-all hover:-translate-y-1 shadow-[0_0_30px_rgba(255,255,255,0.2)]" data-testid="final-cta-btn">
            {tr("Commencer gratuitement", "Start for free")} <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-white/5 py-8 px-6" data-testid="footer">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center">
            <img src={KNORIA_LOGO_URL} alt="KNORIA" className="h-12 object-contain" />
          </div>
          <div className="flex items-center gap-6 text-xs text-slate-500">
            <a href="/terms" className="hover:text-white transition-colors">{tr("Conditions d’utilisation", "Terms of use")}</a>
            <a href="/terms" className="hover:text-white transition-colors">{tr("Politique de confidentialité", "Privacy policy")}</a>
            <span>&copy; 2026 KNORIA. Knowledge Empowered.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
